import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FiArrowLeft, FiPackage, FiTruck, FiCheckCircle, FiClock,
  FiMapPin, FiCreditCard, FiAlertCircle, FiShoppingBag,
  FiCalendar, FiLoader, FiRefreshCw, FiPrinter,
  FiMessageCircle, FiXCircle, FiShield, FiLogIn, FiTag, FiInfo
} from 'react-icons/fi';
import { FaPhoneAlt } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import TrackingModal from '../components/TrackingModal';

// ── IMPORT markOrderConfirmed so review unlocks after order confirm ──
import { markOrderConfirmed } from './ProductDetail';

const API_BASE_URL = "https://server.havbit.in/api";

export default function OrderDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, getToken } = useAuth();

  const [loading, setLoading]                     = useState(true);
  const [order, setOrder]                         = useState(null);
  const [error, setError]                         = useState('');
  const [refreshing, setRefreshing]               = useState(false);
  const [cancelling, setCancelling]               = useState(false);
  const [completingPayment, setCompletingPayment] = useState(false);
  const [verifyingPayment, setVerifyingPayment]   = useState(false);

  const [showTracking, setShowTracking]       = useState(false);
  const [trackingData, setTrackingData]       = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingError, setTrackingError]     = useState('');

  useEffect(() => {
    if (!isAuthenticated) navigate("/login", { state: { from: `/order/${id}` } });
  }, [isAuthenticated, navigate, id]);

  useEffect(() => {
    if (isAuthenticated) fetchOrder();
  }, [id, isAuthenticated]);

  const fetchOrder = async () => {
    try {
      setLoading(true); setError('');
      const token = getToken?.() || localStorage.getItem('token') || '';
      if (!token) throw new Error('Authentication required');
      const res = await fetch(`${API_BASE_URL}/customer/order/${id}`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (!res.ok) throw new Error('Failed to fetch order');
      const data = await res.json();
      if (data.success && data.order) {
        const processed = processOrder(data.order);
        setOrder(processed);

        // ── KEY FIX: Jab bhi order confirm/processing/shipped/delivered ho,
        //    us order ke products ko localStorage mein save karo
        //    taaki review button unlock ho ──
        if (['Confirmed', 'Processing', 'Shipped', 'Delivered'].includes(processed.orderStatus)) {
          const productIds = (processed.orderItems || [])
            .map(item => item.productId || item.product || item._id || item.id)
            .filter(Boolean);
          if (productIds.length > 0) {
            markOrderConfirmed(processed._id, productIds);
          }
        }

        saveLocal(processed);
      } else {
        throw new Error(data.message || 'Order not found');
      }
    } catch (err) {
      try {
        const local = JSON.parse(localStorage.getItem('user_orders') || '[]');
        const found = local.find(o => o._id === id);
        if (found) {
          const processed = processOrder(found);
          setOrder(processed);

          // Local order ke liye bhi check karo
          if (['Confirmed', 'Processing', 'Shipped', 'Delivered'].includes(processed.orderStatus)) {
            const productIds = (processed.orderItems || [])
              .map(item => item.productId || item.product || item._id || item.id)
              .filter(Boolean);
            if (productIds.length > 0) {
              markOrderConfirmed(processed._id, productIds);
            }
          }
        }
        else setError('Order not found. Please check your order ID.');
      } catch {
        setError('Order not found. Please check your order ID.');
      }
    } finally {
      setLoading(false); setRefreshing(false);
    }
  };

  const resolveDeliveryFee = (raw) => {
    if (raw.deliveryFee !== undefined && raw.deliveryFee !== null) return parseFloat(raw.deliveryFee);
    return raw.paymentMethod === 'cod' ? 30 : 0;
  };

  const processOrder = (raw) => {
    const o = { ...raw };
    const sub = o.orderItems?.reduce(
      (t, i) => t + (parseFloat(i.price) || 0) * (parseInt(i.qty) || 1), 0
    ) || 0;
    o.subtotal            = parseFloat(o.subtotal || sub);
    o.deliveryFee         = resolveDeliveryFee(raw);
    o.platformFee         = 0;
    o.couponDiscount      = parseFloat(o.couponDiscount || 0);
    o.totalBeforeDiscount = parseFloat((o.subtotal + o.deliveryFee + o.platformFee).toFixed(2));
    const calc   = o.subtotal + o.deliveryFee + o.platformFee + o.couponDiscount;
    const stored = parseFloat(o.totalPayable || o.amount || 0);
    o.finalAmount = (stored > 0 && Math.abs(stored - calc) < 1) ? stored : parseFloat(calc.toFixed(2));
    return o;
  };

  const saveLocal = (o) => {
    try {
      const all = JSON.parse(localStorage.getItem('user_orders') || '[]');
      if (!all.some(x => x._id === o._id)) {
        all.unshift(o);
        localStorage.setItem('user_orders', JSON.stringify(all.slice(0, 20)));
      }
    } catch {}
  };

  const updateOrderState = (updated) => {
    setOrder(updated);

    // Jab bhi order state update ho (e.g. payment verify ke baad Confirmed ho jae)
    // review bhi unlock karo
    if (['Confirmed', 'Processing', 'Shipped', 'Delivered'].includes(updated.orderStatus)) {
      const productIds = (updated.orderItems || [])
        .map(item => item.productId || item.product || item._id || item.id)
        .filter(Boolean);
      if (productIds.length > 0) {
        markOrderConfirmed(updated._id, productIds);
      }
    }

    try {
      const all = JSON.parse(localStorage.getItem('user_orders') || '[]');
      localStorage.setItem('user_orders', JSON.stringify(all.map(o => o._id === updated._id ? updated : o)));
    } catch {}
  };

  const handleTrackOrder = async () => {
    const awb = order?.parcelx?.awb;
    if (!awb) {
      setTrackingError('AWB number not available yet. Shipment may still be getting processed by ParcelX.');
      setTrackingData(null); setShowTracking(true); return;
    }
    setTrackingLoading(true); setTrackingError(''); setTrackingData(null); setShowTracking(true);
    try {
      const token = getToken?.() || localStorage.getItem('token') || '';
      const res = await fetch(`${API_BASE_URL}/parcelx/track/${awb}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (!res.ok || !data.success) { setTrackingError(data.message || 'Tracking info not available.'); return; }
      const px = data.parcelx_tracking || {}, current = px.current_status || {}, history = px.tracking_history || px.history || [];
      setTrackingData({
        awb,
        status: current.status_title || current.status || order?.parcelx?.status || order?.orderStatus || 'Processing',
        courier: order?.parcelx?.courier || current.courier_name || '',
        expectedDelivery: current.expected_delivery || null,
        origin: current.origin || '',
        destination: current.destination || order?.shippingAddress?.city || '',
        shipmentDetails: { weight: current.weight || order?.shipment?.weight || '', mode: current.mode || 'Surface' },
        history: history.length > 0
          ? history.map(h => ({ status: h.status_title || h.status || '', date: h.event_date || h.date || '', location: h.location || h.city || '', remarks: h.remarks || h.description || '' }))
          : [{ status: current.status_title || order?.orderStatus || 'Order Placed', date: order?.createdAt || new Date().toISOString(), location: '', remarks: 'Shipment booked with ParcelX' }],
      });
    } catch (e) { setTrackingError('Failed to fetch tracking: ' + e.message); }
    finally { setTrackingLoading(false); }
  };

  const handleCloseTracking = () => { setShowTracking(false); setTrackingData(null); setTrackingError(''); };

  const loadRazorpay = () => new Promise((res, rej) => {
    if (window.Razorpay) return res(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => res(true); s.onerror = () => rej(new Error('Failed to load Razorpay'));
    document.body.appendChild(s);
  });

  const handleCompleteRazorpayPayment = async () => {
    if (!order?.razorpayOrderId) return alert('No Razorpay order found');
    try {
      setCompletingPayment(true);
      await loadRazorpay();
      const amount = order.finalAmount || order.amount || 0;
      const rp = new window.Razorpay({
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: Math.round(amount * 100), currency: 'INR',
        name: 'HAVBIT', description: `Order #${order._id?.slice(-8)}`,
        image: havbitLogo, order_id: order.razorpayOrderId,
        handler: async (response) => { await verifyRazorpayPayment(response); },
        prefill: { name: order.shippingAddress?.name || user?.name || '', email: order.shippingAddress?.email || user?.email || '', contact: order.shippingAddress?.phone || user?.phone || '' },
        theme: { color: '#dc2626' },
        modal: { ondismiss: () => setCompletingPayment(false) },
      });
      rp.open();
      rp.on('payment.failed', (r) => { alert(`Payment failed: ${r.error.description}`); setCompletingPayment(false); });
    } catch (e) { alert(`Payment error: ${e.message}`); setCompletingPayment(false); }
  };

  const verifyRazorpayPayment = async (response) => {
    try {
      setVerifyingPayment(true);
      const token = getToken?.() || localStorage.getItem('token') || '';
      const res = await fetch(`${API_BASE_URL}/customer/order/verify-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ razorpay_payment_id: response.razorpay_payment_id, razorpay_order_id: response.razorpay_order_id, razorpay_signature: response.razorpay_signature, orderId: order._id }),
      });
      const data = await res.json();
      if (data.success) {
        updateOrderState({ ...order, paymentStatus: 'Success', orderStatus: 'Confirmed', razorpayPaymentId: response.razorpay_payment_id });
        alert('✅ Payment verified! Order confirmed.');
      }
      else { alert(`❌ Verification failed: ${data.message}`); }
    } catch { alert('Failed to verify payment.'); }
    finally { setVerifyingPayment(false); setCompletingPayment(false); }
  };

  // const handleCancelOrder = async () => {
  //   if (!window.confirm('Are you sure you want to cancel this order?')) return;
  //   try {
  //     setCancelling(true);
  //     const token = getToken?.() || localStorage.getItem('token') || '';
  //     const res = await fetch(`${API_BASE_URL}/customer/order/cancel/${id}`, {
  //       method: 'PUT',
  //       headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  //       body: JSON.stringify({ cancellationReason: 'Customer requested cancellation' }),
  //     });
  //     const data = await res.json();
  //     updateOrderState({ ...order, orderStatus: 'Cancelled', paymentStatus: 'Cancelled' });
  //     alert(data.success ? '✅ Order cancelled!' : '✅ Order cancelled locally!');
  //   } catch {
  //     updateOrderState({ ...order, orderStatus: 'Cancelled', paymentStatus: 'Cancelled' });
  //     alert('✅ Order cancelled!');
  //   } finally { setCancelling(false); }
  // };

  const handleCancelOrder =
  async () => {

    if (
      !window.confirm(
        "Are you sure you want to cancel this order?"
      )
    ) return;

    try {

      setCancelling(true);

      const token =
        getToken?.() ||
        localStorage.getItem(
          "token"
        ) ||
        "";

      const res = await fetch(

        `${API_BASE_URL}/parcelx/cancel-order`,

        {
          method: "POST",

          headers: {
            Authorization:
              `Bearer ${token}`,

            "Content-Type":
              "application/json",
          },

          body: JSON.stringify({

            orderId:
              order._id,
          }),
        }
      );

      const data =
        await res.json();

      if (data.success) {

        updateOrderState({

          ...order,

          orderStatus:
            "Cancelled",

          paymentStatus:
            "Cancelled",
        });

        alert(
          "✅ Order cancelled successfully"
        );

      } else {

        alert(
          data.message ||
          "Cancel failed"
        );
      }

    } catch (err) {

      console.log(err);

      alert(
        "Cancel failed"
      );

    } finally {

      setCancelling(false);
    }
  };


  const handleReturnRequest =
  async () => {

    const reason =
      window.prompt(
        "Enter return reason"
      );

    if (!reason) return;

    try {

      const token =
        getToken?.() ||

        localStorage.getItem(
          "token"
        ) ||

        "";

      const res =
        await fetch(

          `${API_BASE_URL}/parcelx/request-return`,

          {
            method: "POST",

            headers: {

              Authorization:
                `Bearer ${token}`,

              "Content-Type":
                "application/json",
            },

            body: JSON.stringify({

              orderId:
                order._id,

              reason,
            }),
          }
        );

      const data =
        await res.json();

      if (data.success) {

        updateOrderState({

          ...order,

          returnRequested:
            true,

          returnReason:
            reason,

          returnStatus:
            "Requested",

          orderStatus:
            "Return Requested",
        });

        alert(
          "✅ Return request submitted"
        );

      } else {

        alert(
          data.message ||
          "Return request failed"
        );
      }

    } catch (err) {

      console.log(err);

      alert(
        "Return request failed"
      );
    }
  };

  const handlePrintInvoice = () => {
    if (!order) return;

    const {
      subtotal = 0, deliveryFee = 0, platformFee = 0,
      gst = 0, couponDiscount = 0, finalAmount = 0,
    } = order;

    const isCOD = order.paymentMethod === 'cod';

    const sellerName    = order.sellerName    || 'HAVBIT';
    const sellerAddress = order.sellerAddress || 'New Delhi, Delhi 110001';
    const sellerGSTIN   = order.sellerGSTIN   || '07AABCH1234A1ZX';
    const stateCode     = order.shippingAddress?.stateCode || '06';
    const stateName     = order.shippingAddress?.state     || 'Haryana';

    const orderDate = new Date(order.createdAt).toLocaleString('en-IN', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    }).replace(',', '');

    const invoiceNum = order.invoiceNumber || ('INV-' + (order._id?.slice(-8).toUpperCase() || 'XXXXXXXX'));
    const totalIGST  = parseFloat(gst).toFixed(2);

    const itemRows = (order.orderItems || []).map((item, idx) => {
      const price    = parseFloat(item.price || 0);
      const qty      = parseInt(item.qty || 1);
      const grossAmt = price * qty;
      const discount = parseFloat(item.discount || 0);
      const taxable  = grossAmt - discount;
      const igstAmt  = parseFloat((taxable * 5 / 100).toFixed(2));
      const total    = parseFloat((taxable + igstAmt).toFixed(2));
      const hsn      = item.hsn || '630790';
      return `
        <tr>
          <td class="tc b1">${idx + 1}</td>
          <td class="tl b1">${item.productName || 'Product'}</td>
          <td class="tc b1">${hsn}</td>
          <td class="tc b1">${qty}</td>
          <td class="tc b1">Rs.${grossAmt.toFixed(2)}</td>
          <td class="tc b1">Rs.${discount > 0 ? discount.toFixed(2) : '0.00'}</td>
          <td class="tc b1">Rs.${taxable.toFixed(2)}</td>
          <td class="tc b1">Rs.${total.toFixed(2)}</td>
        </tr>`;
    }).join('');

    const deliveryRow = `
      <tr class="xrow">
        <td class="tc b1">—</td>
        <td class="tl b1" colspan="5">${isCOD ? 'Delivery Charge (Cash on Delivery)' : 'Delivery Charge (Online Payment — Free)'}</td>
        <td class="tc b1">${isCOD ? 'Rs.' + parseFloat(deliveryFee).toFixed(2) : 'FREE'}</td>
        <td class="tc b1 ${isCOD ? 'cod-val' : 'free-val'}">${isCOD ? 'Rs.' + parseFloat(deliveryFee).toFixed(2) : 'FREE'}</td>
      </tr>`;

    const couponRow = couponDiscount > 0 ? `
      <tr class="xrow coupon-xrow">
        <td class="tc b1">—</td>
        <td class="tl b1" colspan="5">Coupon Discount${order.couponCode ? ' (' + order.couponCode + ')' : ''}</td>
        <td class="tc b1 green-val">−Rs.${parseFloat(couponDiscount).toFixed(2)}</td>
        <td class="tc b1 green-val">−Rs.${parseFloat(couponDiscount).toFixed(2)}</td>
      </tr>` : '';

    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8"/>
  <title>Invoice - #${invoiceNum}</title>
  <style>
    *{margin:0;padding:0;box-sizing:border-box;}
    body{font-family:Arial,sans-serif;font-size:12px;color:#1a1a1a;background:#fff;padding:30px 36px;max-width:860px;margin:0 auto;}
    .hdr{display:flex;align-items:center;justify-content:space-between;margin-bottom:10px;}
    .logo-wrap{display:flex;align-items:center;gap:8px;}
    .logo-wrap img{height:72px;width:auto;object-fit:contain;transform:scale(1.4);transform-origin:left center;}
    .logo-fb{font-size:32px;font-weight:900;color:#1a1a1a;letter-spacing:-1.5px;font-style:italic;}
    .inv-right{text-align:right;}
    .inv-right h1{font-size:22px;font-weight:800;color:#111;margin-bottom:1px;}
    .inv-right .sub{font-size:10px;color:#888;font-style:italic;}
    hr.red{border:none;border-top:2.5px solid #dc2626;margin:10px 0;}
    hr.lt{border:none;border-top:1px solid #ddd;margin:8px 0;}
    .agrid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:20px;margin:12px 0 10px;}
    .abox h4{font-size:9px;font-weight:800;text-transform:uppercase;color:#777;letter-spacing:1px;margin-bottom:5px;padding-bottom:3px;border-bottom:1.5px solid #ebebeb;}
    .abox p{font-size:11.5px;line-height:1.65;color:#222;}
    .stag{display:inline-block;margin-top:5px;font-size:10px;color:#555;font-style:italic;}
    .meta{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;background:#f9f9f9;border:1px solid #e8e8e8;border-radius:6px;padding:10px 14px;margin:10px 0 14px;}
    .mi label{display:block;font-size:8.5px;text-transform:uppercase;color:#aaa;letter-spacing:0.6px;margin-bottom:3px;}
    .mi span{font-size:11px;font-weight:700;color:#111;word-break:break-all;}
    table.items{width:100%;border-collapse:collapse;margin-bottom:18px;}
    table.items thead tr{background:#f0f0f0;}
    table.items th{padding:8px 5px;font-size:10px;font-weight:700;text-align:center;border:1px solid #ddd;color:#333;}
    table.items th.tl{text-align:left;}
    .b1{border:1px solid #e0e0e0;}
    td{padding:7px 5px;font-size:11px;color:#222;vertical-align:middle;}
    .tc{text-align:center;}
    .tl{text-align:left;}
    .xrow td{background:#fafafa;font-size:10.5px;color:#444;}
    .coupon-xrow td{background:#f0fdf4;}
    .cod-val{color:#c2410c;font-weight:700;}
    .free-val{color:#15803d;font-weight:700;}
    .green-val{color:#15803d;font-weight:700;}
    .grand td{font-weight:900;background:#dc2626;color:#fff;font-size:13.5px;padding:10px 6px;border:1px solid #c00;}
    .terms{font-size:10.5px;color:#555;line-height:1.75;border-top:1px solid #eee;padding-top:14px;margin-top:150px;margin-bottom:20px;}
    .footer{display:flex;justify-content:space-between;align-items:flex-end;border-top:2.5px solid #dc2626;padding-top:10px;margin-top:20px;}
    .footer p{font-size:10px;color:#888;line-height:1.7;}
    .fright{text-align:right;}
    @media print{body{padding:16px;}@page{margin:10mm;}}
  </style>
</head>
<body>
  <div class="hdr">
    <div class="logo-wrap">
    <img src="/vite.png" alt="HAVBIT" style="height:72px;width:auto;object-fit:contain;display:block;" onerror="this.style.display='none';document.getElementById('lf').style.display='block';"/>
      <span id="lf" class="logo-fb" style="display:none">havbit</span>
    </div>
    <div class="inv-right">
      <h1>Tax Invoice</h1>
      <p class="sub">Original For Recipient</p>
    </div>
  </div>
  <hr class="red"/>
  <div class="agrid">
    <div class="abox">
      <h4>Bill To</h4>
      <p><strong>${order.shippingAddress?.name || ''}</strong><br/>${order.shippingAddress?.address || ''},<br/>${order.shippingAddress?.city || ''}, ${order.shippingAddress?.state || ''}, ${order.shippingAddress?.pincode || ''}</p>
      <span class="stag">Place of Supply : ${stateCode} ${stateName}</span>
    </div>
    <div class="abox">
      <h4>Ship To</h4>
      <p><strong>${order.shippingAddress?.name || ''}</strong><br/>${order.shippingAddress?.address || ''}<br/>${order.shippingAddress?.city || ''}, ${order.shippingAddress?.state || ''} - ${order.shippingAddress?.pincode || ''}<br/><strong>${order.shippingAddress?.phone || ''}</strong></p>
    </div>
    <div class="abox">
      <h4>Sold By</h4>
      <p><strong>${sellerName}</strong><br/>${sellerAddress}<br/><span style="font-size:10px;color:#666;">GSTIN: ${sellerGSTIN}</span></p>
    </div>
  </div>
  <hr class="lt"/>
  <div class="meta">
    <div class="mi"><label>Order Number</label><span>${order._id}</span></div>
    <div class="mi"><label>Invoice Number</label><span>${invoiceNum}</span></div>
    <div class="mi"><label>Order Date</label><span>${orderDate}</span></div>
    <div class="mi"><label>Invoice Date</label><span>${orderDate}</span></div>
  </div>
  <table class="items">
    <thead>
      <tr>
        <th style="width:28px">SN.</th>
        <th class="tl">Description</th>
        <th>HSN</th>
        <th>Qty.</th>
        <th>Gross<br/>Amount</th>
        <th>Discount</th>
        <th>Taxable<br/>Value</th>
        <th>Total</th>
      </tr>
    </thead>
    <tbody>
      ${itemRows}
      ${deliveryRow}
      ${couponRow}
      <tr class="grand">
        <td colspan="7" style="text-align:right;padding-right:14px;letter-spacing:0.3px;">Total Payable</td>
        <td class="tc">Rs.${finalAmount.toFixed(2)}</td>
      </tr>
    </tbody>
  </table>
  <div class="terms">
    <strong>Terms &amp; Conditions:</strong><br/>
    Sold by: <strong>${sellerName}</strong>, ${sellerAddress}, GSTIN: ${sellerGSTIN}<br/>
    Tax is not payable on reverse charge basis.<br/>
    This is a computer generated invoice and does not require signature.
  </div>
  <div class="footer">
    <div></div>
    <div class="fright"><p>Thank you for shopping with HAVBIT Global Pvt Ltd.</p></div>
  </div>
  <script>window.onload=()=>{window.print();setTimeout(()=>window.close(),800);}</script>
</body>
</html>`);
    w.document.close();
  };

  /* TRACKING STEPS */
  const STEPS = [
    { status: 'Pending',    label: 'Placed',     icon: <FiClock />,       color: 'bg-gray-500'   },
    { status: 'Confirmed',  label: 'Confirmed',  icon: <FiCheckCircle />, color: 'bg-blue-500'   },
    { status: 'Processing', label: 'Processing', icon: <FiPackage />,     color: 'bg-yellow-500' },
    { status: 'Shipped',    label: 'Shipped',    icon: <FiTruck />,       color: 'bg-purple-500' },
    { status: 'Delivered',  label: 'Delivered',  icon: <FiCheckCircle />, color: 'bg-green-500'  },
  ];
  const stepIndex = Math.max(0, STEPS.findIndex(s => s.status === order?.orderStatus));

  /* GUARDS */
  if (loading) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
      <div className="w-14 h-14 rounded-full border-4 border-gray-100 border-t-red-600 animate-spin mb-4" />
      <p className="text-gray-500 font-medium text-sm">Loading order details...</p>
    </div>
  );

  if (!isAuthenticated) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-sm w-full text-center">
        <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
          <FiLogIn className="text-red-600" size={26} />
        </div>
        <h2 className="text-xl font-bold mb-4 text-gray-900">Login Required</h2>
        <button onClick={() => navigate("/login", { state: { from: `/order/${id}` } })}
          className="w-full py-3 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 transition">
          Login Now
        </button>
      </div>
    </div>
  );

  if (error || !order) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-sm w-full text-center">
        <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
          <FiAlertCircle className="text-red-600" size={26} />
        </div>
        <h2 className="text-xl font-bold mb-2 text-gray-900">Order Not Found</h2>
        <p className="text-gray-500 text-sm mb-6">{error || "This order doesn't exist."}</p>
        <div className="flex flex-col gap-3">
          <button onClick={() => { setRefreshing(true); fetchOrder(); }}
            className="py-3 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 transition flex items-center justify-center gap-2">
            <FiRefreshCw size={14} /> Try Again
          </button>
          <button onClick={() => navigate('/orders')}
            className="py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition">
            View All Orders
          </button>
        </div>
      </div>
    </div>
  );

  const {
    subtotal = 0, deliveryFee = 0, platformFee = 0, gst = 0,
    couponDiscount = 0, totalBeforeDiscount = 0, finalAmount = 0
  } = order;

  const isCOD          = order.paymentMethod === 'cod';
  const isFreeDelivery = deliveryFee === 0;
  const hasAWB         = !!order?.parcelx?.awb;

  // const statusStyle = {
  //   Delivered:  { wrap: 'bg-green-50 border-green-200',   badge: 'bg-green-100 text-green-800'   },
  //   Shipped:    { wrap: 'bg-blue-50 border-blue-200',     badge: 'bg-blue-100 text-blue-800'     },
  //   Processing: { wrap: 'bg-yellow-50 border-yellow-200', badge: 'bg-yellow-100 text-yellow-800' },
  //   Confirmed:  { wrap: 'bg-purple-50 border-purple-200', badge: 'bg-purple-100 text-purple-800' },
  //   Cancelled:  { wrap: 'bg-gray-100 border-gray-300',    badge: 'bg-gray-200 text-gray-700'     },
  // }[order.orderStatus] || { wrap: 'bg-red-50 border-red-200', badge: 'bg-red-100 text-red-800' };



  const statusStyle = {

  Delivered: {
    wrap:
      'bg-green-50 border-green-200',

    badge:
      'bg-green-100 text-green-800',
  },

  Shipped: {
    wrap:
      'bg-blue-50 border-blue-200',

    badge:
      'bg-blue-100 text-blue-800',
  },

  Processing: {
    wrap:
      'bg-yellow-50 border-yellow-200',

    badge:
      'bg-yellow-100 text-yellow-800',
  },

  Confirmed: {
    wrap:
      'bg-purple-50 border-purple-200',

    badge:
      'bg-purple-100 text-purple-800',
  },

  Cancelled: {
    wrap:
      'bg-gray-100 border-gray-300',

    badge:
      'bg-gray-200 text-gray-700',
  },

  "Return Requested": {
    wrap:
      'bg-orange-50 border-orange-200',

    badge:
      'bg-orange-100 text-orange-800',
  },

  "Return Approved": {
    wrap:
      'bg-indigo-50 border-indigo-200',

    badge:
      'bg-indigo-100 text-indigo-800',
  },

  Refunded: {
    wrap:
      'bg-pink-50 border-pink-200',

    badge:
      'bg-pink-100 text-pink-800',
  },

}[order.orderStatus] || {

  wrap:
    'bg-red-50 border-red-200',

  badge:
    'bg-red-100 text-red-800',
};

  return (
    <div className="min-h-screen bg-gray-50 pb-16">

      {/* TRACKING MODAL */}
      {showTracking && (
        <>
          {trackingLoading && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
              <div className="bg-white rounded-2xl p-8 flex flex-col items-center gap-4 shadow-2xl min-w-[240px]">
                <div className="w-12 h-12 rounded-full border-4 border-gray-100 border-t-red-600 animate-spin" />
                <p className="font-bold text-gray-700 text-sm">Fetching tracking info...</p>
                <p className="text-xs text-gray-400 font-mono bg-gray-50 px-3 py-1 rounded-lg">AWB: {order?.parcelx?.awb}</p>
              </div>
            </div>
          )}
          {!trackingLoading && trackingError && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl text-center">
                <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <FiAlertCircle className="text-amber-500" size={26} />
                </div>
                <p className="font-extrabold text-gray-800 text-base mb-2">Tracking Unavailable</p>
                <p className="text-sm text-gray-500 mb-5 leading-relaxed">{trackingError}</p>
                <button onClick={handleCloseTracking} className="w-full py-3 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 transition">Close</button>
              </div>
            </div>
          )}
          {!trackingLoading && !trackingError && trackingData && (
            <TrackingModal trackingData={trackingData} onClose={handleCloseTracking} />
          )}
        </>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">

        {/* Top Bar */}
        <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-700 hover:text-red-600 transition group font-medium text-sm">
              <FiArrowLeft className="group-hover:-translate-x-1 transition-transform" size={16} /> Back
            </button>
            <button onClick={() => { setRefreshing(true); fetchOrder(); }} disabled={refreshing}
              className="flex items-center gap-1.5 text-gray-400 hover:text-red-500 transition text-xs">
              <FiRefreshCw className={refreshing ? 'animate-spin' : ''} size={13} /> Refresh
            </button>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400 font-mono">#{order._id?.slice(-8).toUpperCase()}</span>
            <button onClick={handlePrintInvoice}
              className="flex items-center gap-1.5 px-3.5 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition text-sm font-medium">
              <FiPrinter size={14} /> Invoice
            </button>
          </div>
        </div>

        {/* Status Banner */}
        <div className={`mb-6 p-5 rounded-2xl border ${statusStyle.wrap}`}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-black text-gray-900">Order #{order._id?.slice(-8).toUpperCase()}</h1>
              <div className="flex items-center gap-2 mt-2 text-gray-500 text-sm">
                <FiCalendar size={13} />
                {new Date(order.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </div>
              <div className="mt-2">
                {isCOD ? (
                  <span className="inline-flex items-center gap-1.5 text-xs text-orange-700 bg-orange-100 border border-orange-200 px-2.5 py-1 rounded-lg font-semibold">
                    <FaPhoneAlt size={10} /> Cash on Delivery · ₹30 delivery charge
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-xs text-green-700 bg-green-100 border border-green-200 px-2.5 py-1 rounded-lg font-semibold">
                    <FiCheckCircle size={11} /> Online Payment · Free Delivery
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-row sm:flex-col items-center sm:items-end gap-3">
              <span className={`px-3 py-1.5 rounded-xl font-bold text-xs uppercase tracking-wide ${statusStyle.badge}`}>
                {order.orderStatus || 'Pending'}
              </span>
              <div className="text-right">
                <p className="text-2xl font-black text-gray-900">₹{finalAmount.toFixed(2)}</p>
                {couponDiscount > 0 && <p className="text-xs text-green-600 font-semibold">✓ Saved ₹{couponDiscount.toFixed(2)}</p>}
              </div>
            </div>
          </div>
        </div>

        {/* AWB Banner */}
        {hasAWB && (
          <div className="mb-6 flex items-center justify-between gap-4 px-5 py-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center shrink-0">
                <FiPackage className="text-blue-600" size={18} />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-blue-500 font-bold uppercase tracking-widest">Shipment AWB</p>
                <p className="font-extrabold text-blue-800 text-xl font-mono leading-tight">{order.parcelx.awb}</p>
                {order.parcelx?.courier && <p className="text-xs text-blue-400 mt-0.5">{order.parcelx.courier}</p>}
              </div>
            </div>
            <button onClick={handleTrackOrder}
              className="shrink-0 flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm font-bold rounded-xl hover:bg-blue-700 transition shadow-md shadow-blue-200">
              <FiTruck size={14} /> Track Shipment
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-7">

          {/* LEFT COLUMN */}
          <div className="lg:col-span-2 space-y-5">

            {/* Order Progress */}
            {order.orderStatus !== 'Cancelled' && order.orderStatus !== 'Pending' && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-bold text-gray-900 text-base">Order Progress</h3>
                  <button onClick={handleTrackOrder}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-bold text-red-600 bg-red-50 rounded-xl hover:bg-red-100 transition">
                    <FiTruck size={13} />{hasAWB ? 'Live Tracking' : 'Track Package'}
                  </button>
                </div>
                <div className="relative">
                  <div className="absolute top-[18px] left-0 right-0 h-1.5 bg-gray-100 rounded-full" />
                  <div className="absolute top-[18px] left-0 h-1.5 bg-gradient-to-r from-red-500 to-red-400 rounded-full transition-all duration-700"
                    style={{ width: `${(stepIndex / (STEPS.length - 1)) * 100}%` }} />
                  <div className="relative flex justify-between">
                    {STEPS.map((step, idx) => (
                      <div key={step.status} className="flex flex-col items-center">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center border-4 border-white shadow transition-all z-10 ${idx <= stepIndex ? `${step.color} text-white` : 'bg-gray-200 text-gray-400'}`}>
                          {step.icon}
                        </div>
                        <span className={`mt-2 text-xs font-bold text-center ${idx <= stepIndex ? 'text-gray-800' : 'text-gray-400'}`}>{step.label}</span>
                        {idx === stepIndex && <span className="text-xs text-red-500 font-semibold mt-0.5">Current</span>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Order Items */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <h2 className="font-bold text-base mb-5 flex items-center gap-2 text-gray-900">
                <FiShoppingBag className="text-red-500" size={17} />
                Order Items
                <span className="text-sm text-gray-400 font-medium">({order.orderItems?.length || 0})</span>
              </h2>
              {order.orderItems?.length > 0 ? (
                <div className="divide-y divide-gray-50">
                  {order.orderItems.map((item, idx) => (
                    <div key={idx} className="py-3.5 flex gap-3.5 items-center">
                      <div className="w-16 h-16 bg-gray-50 rounded-xl overflow-hidden border border-gray-100 flex items-center justify-center shrink-0">
                        {item.image
                          ? <img src={item.image} alt={item.productName} className="w-full h-full object-cover" onError={e => { e.target.src = '/placeholder.png'; }} />
                          : <FiPackage className="text-gray-300" size={22} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-gray-900 truncate text-sm">{item.productName || 'Product'}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{item.qty} × ₹{parseFloat(item.price || 0).toFixed(2)}</p>
                      </div>
                      <p className="font-bold text-gray-900 shrink-0 text-sm">
                        ₹{(parseFloat(item.price || 0) * parseInt(item.qty || 1)).toFixed(2)}
                      </p>
                    </div>
                  ))}
                  <div className="pt-3.5 flex justify-between font-bold text-gray-800 text-sm">
                    <span>Items Subtotal</span>
                    <span>₹{subtotal.toFixed(2)}</span>
                  </div>
                </div>
              ) : (
                <div className="text-center py-10 text-gray-400">
                  <FiPackage size={32} className="mx-auto mb-2" />
                  <p className="text-sm">No items found</p>
                </div>
              )}
            </div>

            {/* Payment Info */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <h3 className="font-bold text-base flex items-center gap-2 mb-5 text-gray-900">
                <FiCreditCard className="text-red-500" size={17} /> Payment Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-400 mb-1">Method</p>
                  <p className="font-bold text-sm text-gray-900">
                    {isCOD ? 'Cash on Delivery' : order.paymentMethod === 'razorpay' ? 'Razorpay (Online)' : order.paymentMethod?.toUpperCase() || 'N/A'}
                  </p>
                  {isCOD
                    ? <p className="text-xs text-orange-600 mt-1 flex items-center gap-1"><FiInfo size={10} /> ₹30 delivery charge</p>
                    : <p className="text-xs text-green-600 mt-1 flex items-center gap-1"><FiCheckCircle size={10} /> Free delivery</p>}
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">Status</p>
                  <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${
                    order.paymentStatus === 'Success'   ? 'bg-green-100 text-green-800' :
                    order.paymentStatus === 'Failed'    ? 'bg-red-100 text-red-800'    :
                    order.paymentStatus === 'Cancelled' ? 'bg-gray-100 text-gray-700'  :
                    'bg-yellow-100 text-yellow-800'
                  }`}>{order.paymentStatus || 'Pending'}</span>
                </div>
                {order.razorpayPaymentId && (
                  <div className="col-span-2">
                    <p className="text-xs text-gray-400 mb-1">Payment ID</p>
                    <p className="font-mono text-xs text-gray-600 truncate bg-gray-50 px-3 py-2 rounded-lg">{order.razorpayPaymentId}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-gray-400 mb-1">Order Date</p>
                  <p className="font-bold text-sm text-gray-900">
                    {new Date(order.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN */}
          <div className="space-y-5">

            {/* Shipping Address */}
            {order.shippingAddress && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <h3 className="font-bold text-sm flex items-center gap-2 mb-4 text-gray-900">
                  <FiMapPin className="text-red-500" size={15} /> Shipping Address
                </h3>
                <div className="space-y-1 text-sm">
                  <p className="font-bold text-gray-900">{order.shippingAddress.name}</p>
                  <p className="text-gray-600 leading-relaxed">{order.shippingAddress.address}</p>
                  <p className="text-gray-600">{order.shippingAddress.city}, {order.shippingAddress.state}</p>
                  <p className="text-gray-500">Pincode: {order.shippingAddress.pincode}</p>
                  <p className="text-gray-500 mt-1">{order.shippingAddress.phone}</p>
                  {order.shippingAddress.email && <p className="text-gray-400 text-xs">{order.shippingAddress.email}</p>}
                </div>
              </div>
            )}

            {/* Order Summary */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <h3 className="font-bold text-sm mb-4 text-gray-900">Order Summary</h3>
              <div className="space-y-2.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Subtotal</span>
                  <span className="font-medium text-gray-800">₹{subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-start">
                  <div>
                    <span className="text-gray-500">Delivery</span>
                    {isCOD && <p className="text-xs text-orange-500 mt-0.5">COD charge</p>}
                    {!isCOD && <p className="text-xs text-green-500 mt-0.5">Online payment</p>}
                  </div>
                  <span className={`font-bold ${isFreeDelivery ? 'text-green-600' : 'text-orange-600'}`}>
                    {deliveryFee === 0 ? 'FREE' : `₹${deliveryFee.toFixed(2)}`}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Platform Fee</span>
                  <span className="font-medium text-gray-800">₹{platformFee.toFixed(2)}</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-gray-100">
                  <span className="text-gray-600 font-medium">Before Discount</span>
                  <span className="font-medium text-gray-700">₹{totalBeforeDiscount.toFixed(2)}</span>
                </div>
                {couponDiscount > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 flex items-center gap-1">
                      <FiTag className="text-green-500" size={11} />
                      Coupon
                      {order.couponCode && (
                        <span className="bg-green-100 text-green-700 text-xs px-1.5 py-0.5 rounded ml-1 font-mono">{order.couponCode}</span>
                      )}
                    </span>
                    <span className="font-bold text-green-600">-₹{couponDiscount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                  <span className="font-extrabold text-gray-900">Total Payable</span>
                  <span className="text-xl font-extrabold text-red-600">₹{finalAmount.toFixed(2)}</span>
                </div>
              </div>
              <div className={`mt-4 flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold border ${isCOD ? 'bg-orange-50 border-orange-200 text-orange-700' : 'bg-green-50 border-green-200 text-green-700'}`}>
                {isCOD
                  ? <><FaPhoneAlt size={10} /> Cash on Delivery · ₹30 delivery charge included</>
                  : <><FiCheckCircle size={11} /> Online Payment · Free delivery applied</>}
              </div>
            </div>

            {/* Order Actions */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <h3 className="font-bold text-sm mb-4 text-gray-900">Order Actions</h3>
              <div className="space-y-2.5">
                <button onClick={() => navigate('/contact', { state: { subject: `Order #${order._id}` } })}
                  className="w-full py-2.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition font-medium flex items-center justify-center gap-2 text-sm">
                  <FiMessageCircle size={14} /> Contact Support
                </button>
                {order.paymentMethod === 'razorpay' && order.paymentStatus === 'Pending'
                  && order.orderStatus === 'Pending' && order.razorpayOrderId && (
                  <button onClick={handleCompleteRazorpayPayment} disabled={completingPayment}
                    className="w-full py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition font-bold flex items-center justify-center gap-2 text-sm disabled:opacity-50">
                    {completingPayment ? <><FiLoader className="animate-spin" size={14} /> Opening...</> : <><FiShield size={14} /> Complete Payment</>}
                  </button>
                )}
                {order.orderStatus !== 'Delivered' && order.orderStatus !== 'Cancelled'
                  && order.orderStatus !== 'Shipped' && order.paymentStatus !== 'Success' && (
                  <button onClick={handleCancelOrder} disabled={cancelling}
                    className="w-full py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition font-bold flex items-center justify-center gap-2 text-sm disabled:opacity-50">
                    {cancelling ? <><FiLoader className="animate-spin" size={14} /> Cancelling...</> : <><FiXCircle size={14} /> Cancel Order</>}
                  </button>
                )}
                <button onClick={() => navigate('/products')}
                  className="w-full py-2.5 border-2 border-red-500 text-red-600 rounded-xl hover:bg-red-50 transition font-bold text-sm">
                  Continue Shopping
                </button>
                {/* {order.orderStatus === 'Delivered' && (
                  <button onClick={() => navigate('/products')}
                    className="w-full py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 transition font-bold text-sm">
                    Buy Again
                  </button>
                )}
              </div>
            </div> */}

            {order.orderStatus === 'Delivered' && (
  <button
    onClick={() => navigate('/products')}
    className="w-full py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 transition font-bold text-sm"
  >
    Buy Again
  </button>
)}

{/* RETURN REQUEST */}

{order.orderStatus ===
  "Delivered" &&

!order.returnRequested &&

order.paymentStatus !==
  "Refunded" && (

  <button

    onClick={
      handleReturnRequest
    }

    className="w-full py-2.5 bg-orange-600 text-white rounded-xl hover:bg-orange-700 transition font-bold text-sm"

  >

    Request Return

  </button>
)}

{/* RETURN REQUESTED */}

{order.returnStatus ===
  "Requested" && (

  <div className="w-full py-2.5 bg-orange-100 text-orange-700 rounded-xl text-center font-bold text-sm">

    Return Requested

  </div>
)}
</div>
            </div>

            {/* Help */}
            <div className="bg-gradient-to-br from-red-50 to-orange-50 border border-red-100 rounded-2xl p-5">
              <h4 className="font-bold text-gray-900 text-sm mb-2">Need Help?</h4>
              <div className="text-xs text-gray-500 space-y-1 mt-2">
                <p>📞 +91 12345 67890</p>
                <p className="text-gray-400">Mon–Sat · 9 AM – 6 PM</p>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}